import React, { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useParams, useRouteMatch } from "react-router";
import { NavLink } from "react-router-dom";

import { faHubspot } from "@awesome.me/kit-4c83d50d0d/icons/classic/brands";
import {
	faArrowUpRightFromSquare,
	faBookmark,
	faCheckSquare,
	faCommentAltLines,
	faEllipsisH,
	faMemoPad,
	faXmark,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPrivate, faSalesforceOutline } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import cx from "classnames";

import { closeMeetSidePanel as closeMeetSidePanelFn, inGoogleCalendar, inGoogleMeet } from "~/lib/browserExtension";
import { inVideocallMode } from "~/lib/utils";
import { type TabKey, useAllowedSeriesSidePanelTabs } from "~/meetings/SeriesSidePanel/SeriesSidePanel";
import IconButton, { IconLinkButton } from "~/ui/button/IconButton";
import Menu from "~/ui/menu";

import type { SeriesSidePanel_allowedTabs_stream$key } from "~/__generated__/SeriesSidePanel_allowedTabs_stream.graphql";
import type { SeriesSidePanel_allowedTabs_viewer$key } from "~/__generated__/SeriesSidePanel_allowedTabs_viewer.graphql";
import type { TabsCompanionMode_note$key } from "~/__generated__/TabsCompanionMode_note.graphql";

import styles from "./Tabs.module.css";

const noteFragment = graphql`
	fragment TabsCompanionMode_note on Note {
		title
		viewerCanEdit
		url
	}
`;

const TABS = {
	notes: "Notes",
	"private-notes": "Private Notes",
	"action-items": "Action Items",
	automations: "Automations",
	bookmarks: "Bookmarks",
	hubspot: "Hubspot",
	salesforce: "Salesforce",
	comments: "Comments",
} as const satisfies Record<"notes" | TabKey, string>;
type CompanionModeTabKey = keyof typeof TABS;

interface TabProps {
	tab: CompanionModeTabKey;
	label: string;
}

function Tab({ tab, label }: TabProps) {
	const { noteId } = useParams<{ noteId: string; tab?: CompanionModeTabKey }>();
	const url = useMemo(() => `/companion-mode/${noteId}/${tab}/`, [noteId, tab]);

	return (
		<NavLink to={url} activeClassName={styles.selected} className={styles.tab}>
			{label}
		</NavLink>
	);
}

function MenuItemNavLink({
	icon,
	to,
	children,
	color,
}: {
	icon: IconDefinition;
	to: string;
	children: React.ReactNode;
	color?: string;
}) {
	const isSelected = useRouteMatch({ path: to }) !== null;

	return (
		<Menu.ItemLink icon={icon} to={to} className={cx({ [styles.selected]: isSelected })} color={color}>
			{children}
		</Menu.ItemLink>
	);
}

export default function Tabs({
	viewer,
	stream,
	noteKey,
}: {
	viewer: SeriesSidePanel_allowedTabs_viewer$key;
	stream: SeriesSidePanel_allowedTabs_stream$key;
	noteKey: TabsCompanionMode_note$key;
}) {
	const { noteId } = useParams<{ noteId: string }>();
	const makeTabUrl = useCallback((tab: CompanionModeTabKey) => `/companion-mode/${noteId}/${tab}`, [noteId]);

	const note = useFragment(noteFragment, noteKey);
	const allowedTabs = useAllowedSeriesSidePanelTabs(viewer, stream || null);
	const showCommentsTab = useRouteMatch({ path: "/companion-mode/:noteId/comments/:threadKey" }) !== null;

	const closeCompanionMode = useMemo(() => {
		if (inGoogleMeet) return () => closeMeetSidePanelFn();

		if (inGoogleCalendar) return () => window.parent.postMessage({ type: "EXTENSION_CLOSE_SIDE_PANEL" }, "*");

		return null;
	}, []);

	return (
		<header className={styles.header}>
			<div className={styles.titleRow}>
				<h1 className={styles.noteTitle} title={note.title}>
					{note.title}
				</h1>
				<div className={styles.actions}>
					{!inVideocallMode && note.viewerCanEdit && note.url && (
						<IconLinkButton
							title="Open in new tab"
							icon={faArrowUpRightFromSquare}
							variant="tertiary"
							to={note.url}
							target="_blank"
							rel="noopener"
						/>
					)}
					{closeCompanionMode && (
						<IconButton title="Close" icon={faXmark} variant="tertiary" onClick={closeCompanionMode} />
					)}
				</div>
			</div>
			<div className={styles.tabs}>
				<Tab tab="notes" label="Notes" />
				<Tab tab="private-notes" label="Private notes" />
				<Tab tab="action-items" label="Action items" />
				<Menu mobileHeaderTitle="Tabs">
					<Menu.Trigger asChild>
						<IconButton icon={faEllipsisH} variant="tertiary" className={styles.moreTabsButton} />
					</Menu.Trigger>
					<Menu.Content>
						<MenuItemNavLink icon={faMemoPad} to={makeTabUrl("notes")}>
							Notes
						</MenuItemNavLink>
						<MenuItemNavLink icon={faPrivate} to={makeTabUrl("private-notes")}>
							Private notes
						</MenuItemNavLink>
						<MenuItemNavLink icon={faCheckSquare} to={makeTabUrl("action-items")}>
							Action items
						</MenuItemNavLink>
						<MenuItemNavLink icon={faBookmark} to={makeTabUrl("bookmarks")}>
							Bookmarks
						</MenuItemNavLink>
						{allowedTabs.hubspot && (
							<MenuItemNavLink icon={faHubspot} to={makeTabUrl("hubspot")} color="#FF7A59">
								HubSpot
							</MenuItemNavLink>
						)}
						{allowedTabs.salesforce && (
							<MenuItemNavLink icon={faSalesforceOutline} to={makeTabUrl("salesforce")}>
								Salesforce
							</MenuItemNavLink>
						)}
						{showCommentsTab && (
							<MenuItemNavLink icon={faCommentAltLines} to={makeTabUrl("comments")}>
								Comments
							</MenuItemNavLink>
						)}
					</Menu.Content>
				</Menu>
			</div>
		</header>
	);
}
